import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { SettingsManageUsersRootParamList } from "../../../../types";
import { StaffUserForm, StaffUserFormValues } from "../../components";
import {
  Card,
  Label,
  Page,
  Stack,
  TextField,
  Button,
  useFlashMessages,
  FlatActionList,
} from "@towersystems/roam-common-native";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Colors, View } from "react-native-ui-lib";
import { useMutation } from "@apollo/client";
import { MUTATION_CREATE_STAFF_WITH_USER_ACCOUNT } from "../../common";
import { Staff } from "@towersystems/roam-common/lib/generated-types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { Switch } from "react-native";

export type SettingsCreateStaffMemberScreenProps = NativeStackScreenProps<
  SettingsManageUsersRootParamList,
  "SettingsCreateStaffMemberScreen"
>;

const Content = ({ navigation }: SettingsCreateStaffMemberScreenProps) => {
  const { control, handleSubmit, formState } =
    useFormContext<StaffUserFormValues>();

  const [mutationCreateStaffMember] = useMutation<{
    createStaffWithUserAccount: Staff;
  }>(MUTATION_CREATE_STAFF_WITH_USER_ACCOUNT);

  const flashMessagesContext = useFlashMessages();

  const { t } = useTranslation("settings");

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const handleOnSubmit = async (data: StaffUserFormValues) => {
    try {
      const { fullName, initials, emailAddress, username, newPassword } = data;
      const response = await mutationCreateStaffMember({
        variables: {
          input: {
            fullName,
            initials,
            emailAddress,
            username,
            password: newPassword,
          },
        },
      });

      if (response.data?.createStaffWithUserAccount) {
        flashMessagesContext.showMessage({
          severity: "success",
          message: t("message.staff_member_created"),
        });
        await fetchAndSyncEntity("staffs");
        navigation.goBack();
      }
    } catch (error) {
      flashMessagesContext.showMessage({
        severity: "error",
        message: t("message.error_creating_staff_member"),
      });
      return;
    }
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_staff")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title="Create new user"
      secondaryTitle="Create a new user with staff permissions"
      scrollable={true}
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Details">
          <Stack vertical>
            <View>
              <Label required label={t("form.staff_fullname.label")}></Label>
              <Controller
                name="fullName"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_fullname.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_initials.label")}></Label>
              <Controller
                name="initials"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_initials.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_email.label")}></Label>
              <Controller
                name="emailAddress"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    validate="email"
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_email.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
        <Card title="credentials">
          <Stack vertical>
            <View>
              <Label required label={t("form.staff_username.label")}></Label>
              <Controller
                name="username"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_username.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_password.label")}></Label>
              <Controller
                name="newPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_password.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label
                required
                label={t("form.staff_confirm_password.label")}
              ></Label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_confirm_password.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
        <Card title="other">
          <Stack vertical>
            <FlatActionList
              items={[
                {
                  key: "staff_enabled",
                  title: t("form.staff_enabled.label"),
                  subTitle: t("form.staff_enabled.description"),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export const SettingsCreateStaffMemberScreen = (
  props: SettingsCreateStaffMemberScreenProps,
) => {
  return (
    <StaffUserForm
      defaultFormValues={{
        fullName: "",
        initials: "",
        emailAddress: "",
        username: "",
        newPassword: "",
        confirmPassword: "",
        enabled: true, // defaults to true
      }}
    >
      <Content {...props} />
    </StaffUserForm>
  );
};
